import * as XLSX from 'xlsx';

/**
 * Cabecera que genera el backend en GET /api/download-respondent-template
 * y que espera ExcelInviteeParser (columnas 1..5, en este orden).
 */
export const HEADERS_PLANTILLA = [
  'Documento',
  'Correo',
  'Telefono Celular',
  'Indicativo de Pais',
  'UidEmpresa',
];

/**
 * Columnas 6 y 7 de la plantilla. Pueden faltar: las plantillas anteriores tienen
 * solo cinco y tienen que seguir cargando. Si traen título, tiene que ser el suyo.
 */
export const HEADERS_OPCIONALES = ['Uid2', 'Uid3'];

/** Normaliza un texto para comparar cabeceras: sin tildes, sin espacios extra, minúsculas. */
const normalizarCabecera = (valor) =>
  String(valor ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const textoOrNull = (valor) => {
  const texto = String(valor ?? '').trim();
  return texto === '' ? null : texto;
};

const enteroOrNull = (valor) => {
  const digitos = String(valor ?? '').replace(/\D/g, '');
  if (digitos === '') return null;
  const numero = Number.parseInt(digitos, 10);
  return Number.isNaN(numero) ? null : numero;
};

/**
 * Lee el .xlsx y devuelve las filas crudas con su número de fila real.
 * No valida contenido: eso lo hace analyzeAudience.
 */
export const parseWorkbook = (arrayBuffer) => {
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const worksheet = workbook.Sheets[workbook.SheetNames[0]];
  const rango = XLSX.utils.decode_range(worksheet['!ref'] ?? 'A1:A1');

  /**
   * Lee una celda conservando los errores de fórmula. sheet_to_json los sustituye
   * por el valor por defecto, así que un BUSCARV fallido (#N/D en Excel español,
   * #N/A en el archivo) llegaba como celda vacía y no se podía reportar.
   */
  const leerCelda = (indiceFila, columna) => {
    const celda = worksheet[XLSX.utils.encode_cell({ r: indiceFila, c: columna })];
    if (!celda) return null;
    if (celda.t === 'e') return celda.w ?? '#N/A';
    const valor = celda.w ?? celda.v;
    return valor === undefined || valor === null ? null : String(valor);
  };

  const ultimaColumna = Math.max(rango.e.c, HEADERS_PLANTILLA.length - 1);
  const headersFound = [];
  for (let c = rango.s.c; c <= ultimaColumna; c += 1) {
    headersFound.push((leerCelda(rango.s.r, c) ?? '').trim());
  }

  const opcionalesOk = HEADERS_OPCIONALES.every((esperado, i) => {
    const vino = normalizarCabecera(leerCelda(rango.s.r, HEADERS_PLANTILLA.length + i));
    return vino === '' || vino.replace(/[\s_-]/g, '') === normalizarCabecera(esperado);
  });

  const headerOk =
    opcionalesOk &&
    HEADERS_PLANTILLA.every(
      (esperado, i) => normalizarCabecera(headersFound[i]) === normalizarCabecera(esperado)
    );

  const rows = [];
  for (let r = rango.s.r + 1; r <= rango.e.r; r += 1) {
    const documento = textoOrNull(leerCelda(r, 0));
    const correo = textoOrNull(leerCelda(r, 1))?.toLowerCase() ?? null;
    const celular = textoOrNull(leerCelda(r, 2));
    const indicativo = enteroOrNull(leerCelda(r, 3));
    const uidEmpresa = textoOrNull(leerCelda(r, 4));
    // Informativos: no cuentan para decidir si la fila está vacía.
    const uid2 = textoOrNull(leerCelda(r, 5));
    const uid3 = textoOrNull(leerCelda(r, 6));

    const vacia =
      documento === null &&
      correo === null &&
      celular === null &&
      indicativo === null &&
      uidEmpresa === null;
    if (vacia) continue;

    rows.push({
      fila: r + 1,
      origen: 'excel',
      documento,
      correo,
      celular,
      indicativo,
      uidEmpresa,
      uid2,
      uid3,
    });
  }

  return { headerOk, headersFound, rows };
};

/** Indicativos ofrecidos en el formulario y aceptados sin advertencia. */
export const INDICATIVOS = [
  { code: 57, label: '+57 Colombia' },
  { code: 1, label: '+1 USA/Canadá' },
  { code: 34, label: '+34 España' },
  { code: 52, label: '+52 México' },
  { code: 54, label: '+54 Argentina' },
  { code: 55, label: '+55 Brasil' },
  { code: 56, label: '+56 Chile' },
  { code: 51, label: '+51 Perú' },
];

export const INDICATIVO_POR_DEFECTO = 57;

/**
 * Tope de filas que se parsean en el navegador. Por encima se envía el archivo
 * al backend como antes, sin revisión previa: armar el JSON sería demasiado pesado.
 */
export const MAX_FILAS_NAVEGADOR = 20000;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Valores que Excel o el ERP dejan en una celda cuando el dato no existe.
 * Se tratan como celda vacía: si se dejaran pasar, todas las filas con #N/D
 * se agruparían entre sí como si fueran la misma persona.
 */
const VALORES_CENTINELA = [
  '#n/d',
  '#n/a',
  '#value!',
  '#ref!',
  '#¡div/0!',
  'null',
  'n/a',
  'na',
  '-',
];

const esCentinela = (valor) =>
  valor !== null &&
  valor !== undefined &&
  VALORES_CENTINELA.includes(String(valor).trim().toLowerCase());

/** Mismo criterio que NormCelular del backend: se conservan solo los dígitos. */
export const soloDigitos = (valor) => String(valor ?? '').replace(/\D/g, '');

const tieneIdentificador = (row) =>
  Boolean(row.documento || row.correo || row.celular || row.uidEmpresa);

/**
 * Limpia la fila y devuelve los avisos de lo que se limpió.
 * Nada de esto descarta la fila: solo deja de usarse el dato que no sirve,
 * para que el usuario vea qué se va a cargar realmente.
 */
const sanearFila = (row) => {
  const avisos = [];
  const camposSaneados = [];
  const limpia = { ...row };

  const descartarValor = (campo, mensaje) => {
    avisos.push(mensaje);
    camposSaneados.push(campo);
    limpia[campo] = null;
  };

  ['documento', 'correo', 'celular', 'uidEmpresa'].forEach((campo) => {
    if (!esCentinela(limpia[campo])) return;
    const bruto = String(row[campo]).trim();
    descartarValor(
      campo,
      bruto.startsWith('#')
        ? `${campo}: la celda trae el error de fórmula ${bruto} (en Excel se ve como #N/D); se ignora ese valor`
        : `${campo}: el archivo trae "${bruto}", se ignora ese valor`
    );
  });

  if (limpia.correo && !EMAIL_RE.test(limpia.correo)) {
    descartarValor(
      'correo',
      `correo: "${limpia.correo}" no tiene formato válido, se cargará sin correo`
    );
  }

  if (limpia.celular && soloDigitos(limpia.celular) === '') {
    descartarValor(
      'celular',
      `celular: "${limpia.celular}" no tiene ningún dígito, se cargará sin celular`
    );
  }

  return { limpia, avisos, camposSaneados };
};

/** Observaciones sobre la fila ya saneada. La fila se carga igual. */
const advertenciasDeFila = (row) => {
  const avisos = [];

  if (!row.documento) {
    avisos.push('Sin documento: se identificará solo por correo y UidEmpresa');
  }

  if (!row.correo) {
    avisos.push('Sin correo: se cargará pero no recibirá invitación por email');
  }

  return avisos;
};

/**
 * Una incidencia por fila, con todos sus mensajes y la fila original completa,
 * para que el popup pueda mostrar el registro entero y no solo el campo que falló.
 */
const incidencia = (row, mensajes, accion, camposSaneados = []) => ({
  key: row.key,
  fila: row.fila,
  origen: row.origen,
  row,
  mensajes,
  accion,
  camposSaneados,
});


/** Campos con los que el backend hace match de Respondent (unicidad por encuesta). */
export const CAMPOS_IDENTIDAD = ['documento', 'correo', 'celular', 'uidEmpresa'];

/** Valor comparable de un identificador; null si la fila no lo trae. */
const normalizarIdentificador = (campo, valor) => {
  if (valor === null || valor === undefined || valor === '') return null;
  if (campo === 'celular') {
    const digitos = soloDigitos(valor);
    return digitos === '' ? null : digitos;
  }
  const texto = String(valor).trim().toLowerCase();
  return texto === '' ? null : texto;
};

/** Clave estable de la fila: el número de fila solo es único dentro de su origen. */
const claveDeFila = (row) => `${row.origen}:${row.fila}`;

/** Cuántos datos trae la fila; decide cuál se sugiere en un grupo de duplicados. */
const completitud = (row) =>
  [row.documento, row.correo, row.celular, row.indicativo, row.uidEmpresa, row.uid2, row.uid3].filter(
    (valor) => valor !== null && valor !== undefined && valor !== ''
  ).length;

const crearUnionFind = (claves) => {
  const padre = new Map(claves.map((clave) => [clave, clave]));

  const find = (clave) => {
    let actual = clave;
    while (padre.get(actual) !== actual) {
      padre.set(actual, padre.get(padre.get(actual)));
      actual = padre.get(actual);
    }
    return actual;
  };

  const union = (a, b) => {
    const raizA = find(a);
    const raizB = find(b);
    if (raizA !== raizB) padre.set(raizA, raizB);
  };

  return { find, union };
};

/** Identificadores que aparecen en más de una fila del grupo. */
const clavesCompartidas = (filas) =>
  CAMPOS_IDENTIDAD.flatMap((campo) => {
    const vistos = new Map();
    filas.forEach((row) => {
      const norm = normalizarIdentificador(campo, row[campo]);
      if (norm === null) return;
      const previo = vistos.get(norm);
      if (previo) previo.conteo += 1;
      else vistos.set(norm, { conteo: 1, valor: row[campo] });
    });
    return [...vistos.values()]
      .filter((entrada) => entrada.conteo > 1)
      .map((entrada) => ({ campo, valor: entrada.valor }));
  });

/**
 * Agrupa por componente conexa: si la fila A comparte documento con B y B comparte
 * correo con C, las tres describen al mismo Respondent y van al mismo grupo.
 */
const agruparDuplicados = (rows) => {
  const { find, union } = crearUnionFind(rows.map((row) => row.key));
  const primeraAparicion = new Map();

  rows.forEach((row) => {
    CAMPOS_IDENTIDAD.forEach((campo) => {
      const norm = normalizarIdentificador(campo, row[campo]);
      if (norm === null) return;
      const indice = `${campo}:${norm}`;
      const previa = primeraAparicion.get(indice);
      if (previa) union(previa, row.key);
      else primeraAparicion.set(indice, row.key);
    });
  });

  const porRaiz = new Map();
  rows.forEach((row) => {
    const raiz = find(row.key);
    if (!porRaiz.has(raiz)) porRaiz.set(raiz, []);
    porRaiz.get(raiz).push(row);
  });

  const groups = [];
  const sueltas = [];

  [...porRaiz.values()].forEach((filas) => {
    if (filas.length === 1) {
      sueltas.push(filas[0]);
      return;
    }
    const sugerida = filas.reduce((mejor, row) =>
      completitud(row) > completitud(mejor) ? row : mejor
    );
    groups.push({
      id: `g${groups.length + 1}`,
      claves: clavesCompartidas(filas),
      filas,
      sugerida: sugerida.key,
    });
  });

  return { groups, cleanRows: sueltas };
};

/**
 * Aplica las reglas a las filas ya parseadas (Excel + formulario), separa las
 * que hay que resolver por duplicado y devuelve una incidencia por fila.
 *
 * Una fila solo se descarta si, después de limpiarla, no queda ningún
 * identificador con el que el backend pueda crear el Respondent.
 */
export const analyzeAudience = (rows) => {
  const detalle = [];
  const validas = [];

  rows.forEach((original, indice) => {
    const conClave = { ...original, key: claveDeFila(original), indice };
    const { limpia, avisos, camposSaneados } = sanearFila(conClave);
    const utilizable = tieneIdentificador(limpia);

    const mensajes = utilizable
      ? [...avisos, ...advertenciasDeFila(limpia)]
      : [...avisos, 'La fila no tiene documento, correo, celular ni UidEmpresa utilizables'];

    detalle.push(
      incidencia(conClave, mensajes, utilizable ? 'carga' : 'descarta', camposSaneados)
    );
    if (utilizable) validas.push(limpia);
  });

  const { groups, cleanRows } = agruparDuplicados(validas);

  return {
    totalFilas: rows.length,
    detalle,
    errors: detalle.filter((d) => d.accion === 'descarta'),
    warnings: detalle.filter((d) => d.accion === 'carga' && d.mensajes.length > 0),
    groups,
    cleanRows,
  };
};

/** Valor de selección que indica "no cargar ninguna fila de este grupo". */
export const DESCARTAR_GRUPO = '__descartar__';

/**
 * Arma la lista final: filas sin duplicado + la ganadora de cada grupo,
 * en el orden en que venían originalmente.
 */
export const resolveAudience = ({ cleanRows, groups }, selecciones = {}) => {
  const ganadoras = groups
    .map((grupo) => {
      const elegida = selecciones[grupo.id] ?? grupo.sugerida;
      if (elegida === DESCARTAR_GRUPO) return null;
      return grupo.filas.find((row) => row.key === elegida) ?? null;
    })
    .filter(Boolean);

  return [...cleanRows, ...ganadoras].sort((a, b) => a.indice - b.indice);
};

/** Convierte las filas resueltas al shape de JourneyMailRow que espera el backend. */
export const toPayloadRows = (rows) =>
  rows.map((row) => ({
    documento: row.documento ?? '',
    UidEmpresa: row.uidEmpresa ?? '',
    Uid2: row.uid2 ?? '',
    Uid3: row.uid3 ?? '',
    correo: row.correo ?? '',
    celular: row.celular ? soloDigitos(row.celular) : null,
    indicativo: row.indicativo ?? INDICATIVO_POR_DEFECTO,
  }));

/** Lee una propiedad tolerando camelCase o PascalCase. */
const leer = (objeto, nombre) => {
  if (!objeto) return undefined;
  const pascal = nombre.charAt(0).toUpperCase() + nombre.slice(1);
  return objeto[nombre] ?? objeto[pascal];
};

/**
 * Traduce el BulkMailResult del backend a las mismas estructuras del popup.
 * ParseRowsFromJourneyMail reasigna NumeroFila = i + 2, así que hay que
 * devolverle al usuario el número de fila de su propio archivo.
 */
export const mapBackendResult = (resultado, filasEnviadas = []) => {
  const filaOriginal = (numeroFila) => filasEnviadas[numeroFila - 2] ?? null;

  const aIncidencia = (item, accion) => {
    const numeroFila = leer(item, 'numeroFila');
    const original = filaOriginal(numeroFila);
    return {
      key: original?.key ?? `backend:${numeroFila}`,
      fila: original ? original.fila : numeroFila,
      origen: original ? original.origen : 'excel',
      row: original ?? {
        documento: leer(item, 'documento') ?? null,
        correo: leer(item, 'correo') ?? null,
        celular: leer(item, 'celular') ?? null,
        indicativo: null,
        uidEmpresa: leer(item, 'uidEmpresa') ?? null,
        uid2: null,
        uid3: null,
      },
      mensajes: [leer(item, 'motivo') ?? ''],
      accion,
    };
  };

  return {
    resumen: {
      totalFilas: leer(resultado, 'totalFilas') ?? 0,
      procesadas: leer(resultado, 'procesadas') ?? 0,
      emailsEnviados: leer(resultado, 'emailsEnviados') ?? 0,
      sinEmail: leer(resultado, 'sinEmail') ?? 0,
      yaRespondidas: leer(resultado, 'yaRespondidas') ?? 0,
      duplicadasEnArchivo: leer(resultado, 'duplicadasEnArchivo') ?? 0,
      correosInvalidos: leer(resultado, 'correosInvalidos') ?? 0,
      fallidasOmitidas: leer(resultado, 'fallidasOmitidas') ?? 0,
      advertenciasOmitidas: leer(resultado, 'advertenciasOmitidas') ?? 0,
      cargaBloqueada: leer(resultado, 'cargaBloqueada') ?? false,
    },
    // Fallidas = la fila no quedó cargada, hay que corregirla y volver a subir.
    errors: (leer(resultado, 'fallidas') ?? []).map((i) => aIncidencia(i, 'descarta')),
    // Advertencias = la fila SÍ quedó cargada, solo hay algo que conviene saber.
    // Los conflictos van al mismo grupo: son duplicados que el backend ya resolvió
    // contra la base, sin perder a nadie.
    warnings: [
      ...(leer(resultado, 'advertencias') ?? []),
      ...(leer(resultado, 'conflictos') ?? []),
    ].map((i) => aIncidencia(i, 'carga')),
  };
};
